import React, { useState, useCallback } from "react";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import Loader from "shared/Icons/Loader/Loader";
import { SecondaryButton } from "widgets";
import { TENANT_STATUS } from "constants/constants";
import { useNavigate } from "react-router-dom";
import {
  formatDisplayValue,
  formatGradeLabel,
  normalizeAddressFields,
  useTenantDetailsLookups,
} from "helpers/tenantUtils";
import { VIEW_DASH } from "constants/schoolConstants";

import styles from "./ViewTenantDetails.module.scss";

const dash = VIEW_DASH;

const formatDateTime = (iso) => {
  if (!iso) return dash;
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return String(iso);
  }
};

const statusLabel = (status) => {
  if (!status) return dash;
  if (status === TENANT_STATUS.CREATED) return "Created";
  if (status === "PUBLISHED") return "Published";
  return status.replace(/_/g, " ");
};

const statusTone = (status) => {
  if (status === TENANT_STATUS.CREATED) return styles.statusCreated;
  if (status === "PUBLISHED") return styles.statusPublished;
  return styles.statusDefault;
};

const handleOnClickCreateBranchButton = (
  navigate,
  tenantId,
  tenantName,
  tenantCode,
  gradeList,
) => {
  navigate(`/createBranch?tenantId=${tenantId}`, {
    state: {
      tenantName,
      tenantCode,
      gradeList,
    },
  });
};

const Section = ({ icon: Icon, title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionHeader}>
      {Icon ? <Icon className={styles.sectionIcon} aria-hidden /> : null}
      <h3 className={styles.sectionTitle}>{title}</h3>
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const Field = ({ label, value, fullWidth, isLoading }) => (
  <div className={`${styles.field} ${fullWidth ? styles.fieldFull : ""}`}>
    <div className={styles.fieldLabel}>{label}</div>
    <div className={styles.fieldValue}>
      {isLoading ? "Loading…" : formatDisplayValue(value)}
    </div>
  </div>
);

const FieldGrid = ({ children }) => (
  <div className={styles.fieldGrid}>{children}</div>
);

const TagListField = ({ label, labels, isLoading, fullWidth = false }) => (
  <div className={`${styles.field} ${fullWidth ? styles.fieldFull : ""}`}>
    <div className={styles.fieldLabel}>{label}</div>
    <div className={styles.tagList}>
      {isLoading ? (
        <span className={styles.fieldValueMuted}>Loading…</span>
      ) : labels?.length ? (
        labels.map((labelText) => (
          <span key={labelText} className={styles.gradeTag}>
            {labelText}
          </span>
        ))
      ) : (
        <span className={styles.fieldValueMuted}>{dash}</span>
      )}
    </div>
  </div>
);

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
};

const TenantIdField = ({ tenantId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!tenantId) return;
    try {
      await copyTextToClipboard(tenantId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [tenantId]);

  return (
    <div className={`${styles.field} ${styles.fieldFull}`}>
      <div className={styles.fieldLabel}>Tenant ID</div>
      <div className={styles.tenantIdRow}>
        <span className={styles.tenantIdValue}>{tenantId || dash}</span>
        <button
          type="button"
          className={styles.copyIdButton}
          onClick={handleCopy}
          disabled={!tenantId}
          aria-label="Copy tenant ID to clipboard"
        >
          <ContentCopyOutlinedIcon className={styles.copyIdIcon} aria-hidden />
        </button>
        {copied ? (
          <span className={styles.copiedBadge} role="status">
            Copied
          </span>
        ) : null}
      </div>
    </div>
  );
};

const ViewTenantDetails = ({ tenantDetails }) => {
  const navigate = useNavigate();
  const {
    lookups,
    isCountryListLoading,
    isPostalLookupLoading,
    isPostalError,
    shouldFetchPostal,
  } = useTenantDetailsLookups(tenantDetails);

  const gradeList = tenantDetails?.gradeIds || [];
  const allowSingleBranch = tenantDetails?.hasSingleBranch;

  if (!tenantDetails) return null;

  const address = normalizeAddressFields(tenantDetails.address);
  const taxRegistrationNumber =
    tenantDetails.taxRegistrationNumber ||
    tenantDetails.tax_registration_number;

  const boolIcon = (value) =>
    value ? (
      <span className={styles.boolYes}>
        <CheckCircleOutlineIcon className={styles.boolIcon} aria-hidden />
        Yes
      </span>
    ) : (
      <span className={styles.boolNo}>
        <HighlightOffOutlinedIcon className={styles.boolIcon} aria-hidden />
        No
      </span>
    );

  return (
    <div className={styles.pageCard}>
      <header className={styles.hero}>
        <div className={styles.heroMain}>
          <div className={styles.logoMark} aria-hidden>
            <BusinessOutlinedIcon className={styles.logoMarkIcon} />
          </div>
          <div className={styles.heroText}>
            <h2 className={styles.heroTitle}>{tenantDetails.name}</h2>
            <div className={styles.heroMeta}>
              <span className={styles.codeChip}>{tenantDetails.code}</span>
              <span
                className={`${styles.statusBadge} ${statusTone(
                  tenantDetails.status,
                )}`}
              >
                {statusLabel(tenantDetails.status)}
              </span>
            </div>
          </div>
        </div>
        {!allowSingleBranch ? (
          <SecondaryButton
            onClick={() =>
              handleOnClickCreateBranchButton(
                navigate,
                tenantDetails.id,
                tenantDetails.name,
                tenantDetails.code,
                gradeList,
              )
            }
          >
            Create branch
          </SecondaryButton>
        ) : null}
      </header>

      <Section icon={InfoOutlinedIcon} title="Tenant information">
        <FieldGrid>
          <TenantIdField tenantId={tenantDetails.id} />
          <Field
            label="Vertical"
            value={lookups.vertical?.value}
            isLoading={lookups.vertical?.isLoading}
          />
          <Field label="Tenant code" value={tenantDetails.code} />
          <Field
            label="Country"
            value={lookups.country?.value}
            isLoading={lookups.country?.isLoading || isCountryListLoading}
          />
          <TagListField
            label="Currency"
            labels={lookups.currencies?.labels}
            isLoading={lookups.currencies?.isLoading || isCountryListLoading}
          />
          <TagListField
            label="Timezone"
            labels={lookups.timezones?.labels}
            isLoading={lookups.timezones?.isLoading || isCountryListLoading}
          />
          <Field label="Logo URL" value={tenantDetails.logo} />
          <Field
            label="Created at"
            value={formatDateTime(tenantDetails.createdAt)}
          />
        </FieldGrid>
      </Section>

      <Section icon={PersonOutlineOutlinedIcon} title="Contact">
        <FieldGrid>
          <Field label="Name" value={tenantDetails.contact?.name} />
          <Field label="Phone" value={tenantDetails.contact?.phone} />
          <Field
            label="ISD code"
            value={lookups.isdCode?.value}
            isLoading={lookups.isdCode?.isLoading || isCountryListLoading}
          />
          <Field
            label="Designation"
            value={tenantDetails.contact?.designation}
          />
        </FieldGrid>
      </Section>

      <Section icon={MailOutlineOutlinedIcon} title="Communication">
        <FieldGrid>
          <Field label="Email" value={tenantDetails.email} />
          <Field
            label="Ambassador email"
            value={tenantDetails.ambassadorMail}
          />
          {lookups.isIndia ? (
            <Field label="GSTIN" value={tenantDetails.gstin} />
          ) : (
            <Field
              label="Tax registration number"
              value={taxRegistrationNumber}
            />
          )}
        </FieldGrid>
      </Section>

      <Section icon={LocationOnOutlinedIcon} title="Address">
        {shouldFetchPostal && isPostalLookupLoading ? (
          <div className={styles.inlineLoader}>
            <Loader />
            <span>Loading location details…</span>
          </div>
        ) : null}
        {isPostalError ? (
          <p className={styles.softWarning} role="status">
            Location lookup unavailable. Showing saved address fields only.
          </p>
        ) : null}
        <FieldGrid>
          <Field
            label="Address line 1"
            fullWidth
            value={address.addressLine1}
          />
          <Field
            label="Address line 2"
            fullWidth
            value={address.addressLine2}
          />
          <Field label="Street" value={address.street} />
          <Field label="City" value={address.city} />
          <Field
            label="District"
            value={lookups.district?.value}
            isLoading={lookups.district?.isLoading}
          />
          <Field
            label="State"
            value={lookups.state?.value}
            isLoading={lookups.state?.isLoading}
          />
          <Field label="PIN code" value={address.pinCode} />
          <Field
            label="Area"
            value={lookups.isIndia ? null : address.area}
          />
        </FieldGrid>
      </Section>

      <Section icon={SchoolOutlinedIcon} title="Academic & branches">
        <FieldGrid>
          <div className={`${styles.field} ${styles.gradesField}`}>
            <div className={styles.gradesLabel}>Grades</div>
            <div className={styles.tagList}>
              {tenantDetails.gradeIds?.length ? (
                tenantDetails.gradeIds.map((gradeId) => {
                  const gradeLookup = lookups.grades[gradeId];
                  return (
                    <span key={gradeId} className={styles.gradeTag}>
                      {gradeLookup?.isLoading
                        ? "Loading…"
                        : (gradeLookup?.value ?? formatGradeLabel(gradeId))}
                    </span>
                  );
                })
              ) : (
                <span className={styles.fieldValueMuted}>{dash}</span>
              )}
            </div>
          </div>
          <Field
            label="Branches"
            value={tenantDetails.branchesCount ?? "0"}
          />
        </FieldGrid>
      </Section>

      <Section icon={TuneOutlinedIcon} title="Configuration">
        <FieldGrid>
          <Field
            label="Separate profile"
            value={boolIcon(tenantDetails.profileSeparate)}
          />
          <Field
            label="Single branch tenant"
            value={boolIcon(tenantDetails.hasSingleBranch)}
          />
        </FieldGrid>
      </Section>
    </div>
  );
};

export default ViewTenantDetails;
